import React from "react";
import styled from "styled-components";

import { ParametersTable, ControlsVerticalMenu } from "components/shared";
import LiveControlButtons from "containers/live/LiveControlButtons";
import WeatherForecast from "containers/weather/WeatherForecast";
import { shouldDisplayParam, dec2bin } from "utils/params";

const Wrapper = styled.div``;

const formatParam = (p, liveParams) => {
  const key = p.dbname || p.name;
  const liveParam = liveParams[key.toLowerCase()];

  let val = "";
  // PF2 V0.9.40 vibration_alarm is a 0/1 flag, not a measurement - show
  // it as text rather than "0.00"/"1.00".
  if (key.toLowerCase() === "vibration_alarm") {
    if (liveParam === undefined) return { name: p.name, value: "" };
    return { name: p.name, value: Number(liveParam) ? "ALARM" : "OK" };
  }
  if (liveParam !== undefined) {
    val = typeof liveParam === "number" ? liveParam.toFixed(2) : `${liveParam}`;
  }
  if (p.unit) {
    val = `${val} ${p.unit}`;
  }
  return { name: p.name, value: val };
};

const getFormattedData = (params, liveParams, key) => {
  if (!params || !params[key]) return [];
  return params[key]
    .filter(shouldDisplayParam)
    .map((p) => formatParam(p, liveParams));
};

// Looks up a single param by dbname within one category - used to pull
// grid_frequency into the VIBRATIONS table too (2026-10-04 request), without
// duplicating it out of the GRID section.
const getFormattedParam = (params, liveParams, category, dbname) => {
  if (!params || !params[category]) return null;
  const p = params[category].find((x) => x.dbname === dbname);
  return p ? formatParam(p, liveParams) : null;
};

const getDIDOFormattedData = (params, liveParams, key) => {
  if (!params || !params[key] || !params[key] || !liveParams) return [];
  if (
    key === "DI" &&
    liveParams["di15_00"] === undefined &&
    liveParams["di23_16"] === undefined
  ) {
    return null;
  }
  if (key === "DO" && liveParams["digo"] === undefined) {
    return null;
  }

  const paramsSi15_00 = dec2bin(liveParams["di15_00"], 16).split("").reverse();
  const paramDi23_16 = dec2bin(liveParams["di23_16"], 8).split("").reverse();
  const paramDigO = dec2bin(liveParams["digo"], 16).split("").reverse();
  const allBinaryDiValues =
    key === "DI" ? paramsSi15_00.concat(paramDi23_16) : paramDigO;

  return params[key].map((p, position) => {
    let valKey = "value = 0";
    // Schema DI/DO entries carry their bit position (`index`); the list can
    // have gaps (nordtank300 has no DI 02 / DI 21), so the list position is
    // only a fallback for the legacy xlsx path, which has no index.
    const bit = Number.isInteger(p.index) ? p.index : position;
    const liveParam = allBinaryDiValues[bit];
    if (liveParam > 0) {
      valKey = "value = 1";
    }
    return {
      name: p.name,
      connector: p.connector,
      pin: p.pin,
      description: p.description,
      value: p[valKey],
    };
  });
};

const ditest = () => {
  alert("haide ma");
};

const LiveControlsMenu = ({ windTurbine, turbineParams, liveParams }) => {
  const schemaId = windTurbine.type && windTurbine.type.schemaId;
  // Vestas turbines have no DI/DO registers in their schema (real Vestas
  // controllers don't expose that concept over this link) - the sections
  // just showed "No value provided" forever, so hide them entirely rather
  // than leave a dead tab (2026-09-23 request).
  const isVestas = schemaId === "vestas";

  const render2ColumnsTable = (key, params) => {
    return (
      <ParametersTable key={`table:${key}`} boldIndexes={[0]} params={params} />
    );
  };

  const gridData = getFormattedData(turbineParams, liveParams, "grid");
  const rpmsData = [
    ...getFormattedData(turbineParams, liveParams, "revolution"),
    ...getFormattedData(turbineParams, liveParams, "revolutions"),
  ];
  const temperatureData = getFormattedData(
    turbineParams,
    liveParams,
    "temperature"
  );
  const gridFrequencyData = getFormattedParam(
    turbineParams,
    liveParams,
    "grid",
    "grid_frequency"
  );
  // Value only, no "Grid frequency" label here (2026-10-04 request) - the
  // labelled row already lives in GRID.
  const vibrationsData = [
    ...getFormattedData(turbineParams, liveParams, "vibration"),
    ...(gridFrequencyData ? [{ ...gridFrequencyData, name: "" }] : []),
  ];
  const windData = getFormattedData(turbineParams, liveParams, "wind");
  const conditionData = getFormattedData(
    turbineParams,
    liveParams,
    "condition"
  );
  const meterData = getFormattedData(turbineParams, liveParams, "meter");
  const diData = getDIDOFormattedData(turbineParams, liveParams, "DI");
  const doData = getDIDOFormattedData(turbineParams, liveParams, "DO");

  const CONTROLS = [
    {
      key: "CONTROLS",
      title: "CONTROLS",
      content: <LiveControlButtons windTurbine={windTurbine} />,
    },
    {
      key: "DIGITAL INPUTS",
      title: "DIGITAL INPUTS",
      content: (
        <Wrapper>
          {diData != null ? (
            <ParametersTable
              onClick={ditest}
              key="input"
              boldIndexes={[4]}
              params={diData}
              columnNames={["Name", "Connector", "Pin", "Description", "Value"]}
              highlightOnChange
            />
          ) : (
            "No value provided  "
          )}
        </Wrapper>
      ),
    },
    {
      key: "DIGITAL OUTPUTS",
      title: "DIGITAL OUTPUTS",
      content: (
        <Wrapper>
          {doData != null ? (
            <ParametersTable
              key="output"
              boldIndexes={[4]}
              columnNames={["Name", "Connector", "Pin", "Description", "Value"]}
              params={doData}
              highlightOnChange
            />
          ) : (
            "No value provided"
          )}
        </Wrapper>
      ),
    },
    {
      key: "GRID",
      title: "GRID",
      content: render2ColumnsTable("GRID", gridData),
    },
    {
      key: "RPMS",
      title: "RPMS",
      content: render2ColumnsTable("RPMS", rpmsData),
    },
    {
      key: "TEMPERATURE",
      title: "TEMPERATURE",
      content: render2ColumnsTable("TEMPERATURE", temperatureData),
    },
    {
      key: "VIBRATIONS",
      title: "VIBRATIONS",
      content: render2ColumnsTable("VIBRATIONS", vibrationsData),
    },
    {
      key: "WIND",
      title: "WIND",
      content: render2ColumnsTable("WIND", windData),
    },
    {
      key: "CONDITION",
      title: "CONDITION",
      content: render2ColumnsTable("CONDITION", conditionData),
    },
    {
      key: "METER",
      title: "METER",
      content: render2ColumnsTable("METER", meterData),
    },
    {
      title: "WEATHER FORECAST",
      content: (
        <WeatherForecast
          coordinates={{
            latitude: windTurbine.latitude,
            longitude: windTurbine.longitude,
          }}
        />
      ),
    },
  ];

  const visibleControls = isVestas
    ? CONTROLS.filter(
        (c) => c.key !== "DIGITAL INPUTS" && c.key !== "DIGITAL OUTPUTS"
      )
    : CONTROLS;

  return (
    <ControlsVerticalMenu controls={visibleControls} windTurbine={windTurbine} />
  );
};

LiveControlsMenu.propTypes = {};

LiveControlsMenu.defaultProps = {};

export default React.memo(LiveControlsMenu);
